/**
 * Description Step (Wizard step 5, Spec 6).
 *
 * One large auto-growing Textarea holding the YAML top-level `description` block
 * — the multi-section markdown document that teaches the Bluey LLM polisher when
 * and how to use the tool. Users can generate it with AI (a swappable
 * DescriptionAdvisor) or write/edit it by hand.
 *
 * "Suggest description with AI" calls the advisor. If the Textarea already has
 * text, a confirmation modal gates the replace to avoid silently wiping manual
 * edits (propose-confirm pattern — mirrors ParametersStep). A read-only
 * "sections included" checklist surfaces which sections the AI emitted so the
 * user can spot omissions.
 *
 * Form fields only — no direct API/AWS (the advisor owns any network).
 */

import React, { useRef, useState } from 'react';
import {
  SpaceBetween,
  Container,
  Header,
  Button,
  Textarea,
  Box,
  Alert,
  Modal,
  StatusIndicator,
} from '@amzn/awsui-components-react';

import {
  DescriptionAdvisor,
  DescriptionSuggestion,
  OnboardingConfig,
  ColumnInfo,
} from '../types';
import { OnboardingAction } from '../onboardingReducer';
import { heuristicDescriptionAdvisor } from '../llmDescriptionAdvisor';

export interface DescriptionStepProps {
  config: OnboardingConfig;
  columns?: ColumnInfo[];
  dispatch: React.Dispatch<OnboardingAction>;
  /** Override the default heuristicDescriptionAdvisor (for testing / the LLM advisor). */
  advisor?: DescriptionAdvisor;
}

// ─── Component ───────────────────────────────────────────────────────────

const DescriptionStep: React.FC<DescriptionStepProps> = ({
  config,
  columns,
  dispatch,
  advisor = heuristicDescriptionAdvisor,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [sections, setSections] = useState<string[]>([]);
  const [pendingSuggestion, setPendingSuggestion] = useState<DescriptionSuggestion | null>(null);

  // Mirror the latest description into a ref so the async handleSuggest reads the
  // CURRENT text at resolution time, not the value captured when it was invoked.
  // Without this, text typed during the (up to a minute) AI call would be
  // invisible to the closure's has-data check, bypassing the replace-confirm
  // modal and silently overwriting the manual edit.
  const descriptionRef = useRef(config.toolDescription);
  descriptionRef.current = config.toolDescription;

  const applySuggestion = (suggestion: DescriptionSuggestion) => {
    dispatch({ type: 'SET_TOOL_DESCRIPTION', value: suggestion.description });
    setWarnings(suggestion.warnings);
    setSections(suggestion.sections ?? []);
  };

  const handleSuggest = async () => {
    setError(null);
    setWarnings([]);
    setSections([]);
    setLoading(true);
    try {
      const suggestion = await advisor.suggest({
        columns: columns ?? [],
        businessContext: config.businessContext,
        columnNotes: config.columnNotes,
        confirmedBaseMetrics: config.baseMetrics,
        confirmedDerivedMetrics: config.derivedMetrics,
        parameters: config.parameters,
      });
      if (descriptionRef.current.trim().length > 0) {
        setPendingSuggestion(suggestion);
      } else {
        applySuggestion(suggestion);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Suggestion failed');
    } finally {
      setLoading(false);
    }
  };

  // Auto-grow the Textarea: compute rows from newline count + char length so the
  // box tracks the (long, multi-section) description. Clamped 8–40.
  const desc = config.toolDescription || '';
  const lineCount = (desc.match(/\n/g) || []).length + 1;
  const charLines = Math.ceil(desc.length / 100);
  const rows = Math.min(40, Math.max(8, Math.max(lineCount, charLines)));

  return (
    <SpaceBetween size="l">
      {/* Confirmation modal for a non-empty description */}
      {pendingSuggestion && (
        <Modal
          visible={true}
          header="Replace existing description?"
          onDismiss={() => setPendingSuggestion(null)}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setPendingSuggestion(null)}>Cancel</Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    applySuggestion(pendingSuggestion);
                    setPendingSuggestion(null);
                  }}
                >
                  Replace
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          This will replace your current description with the AI suggestion.
        </Modal>
      )}

      {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}
      {warnings.length > 0 && (
        <Alert type="warning" dismissible onDismiss={() => setWarnings([])}>
          {warnings.join(' • ')}
        </Alert>
      )}

      <Container
        header={
          <Header
            variant="h2"
            description="The description teaches the Bluey LLM when and how to use this tool — its purpose, scope, decision logic, and parameter rules. Generate a draft with AI, then edit it freely."
            actions={
              <Button
                iconName="gen-ai"
                onClick={handleSuggest}
                disabled={loading}
              >
                {loading ? (
                  <StatusIndicator type="loading">Generating with AI — this can take up to a minute…</StatusIndicator>
                ) : (
                  'Suggest description with AI'
                )}
              </Button>
            }
          >
            Description
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Textarea
            value={config.toolDescription}
            rows={rows}
            onChange={({ detail }) => dispatch({ type: 'SET_TOOL_DESCRIPTION', value: detail.value })}
            placeholder="Generate a description with AI or write one here…"
            ariaLabel="Tool description"
          />
          {sections.length > 0 && (
            <Box>
              <Box variant="awsui-key-label">Sections included</Box>
              <SpaceBetween size="xxs">
                {sections.map((s) => (
                  <StatusIndicator key={s} type="success">{s}</StatusIndicator>
                ))}
              </SpaceBetween>
            </Box>
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

export default DescriptionStep;
